import React, { useState, useEffect } from "react";
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    Title,
    Tooltip,
    Legend,
} from 'chart.js';
import { Line } from 'react-chartjs-2';
import Sidebar from "../components/Sidebar";
import Navbar from "../components/Navbar";
import useAuth from '../hooks/useAuth';

ChartJS.register(
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    Title,
    Tooltip,
    Legend
);

const MonthlyData = () => {
    const API_URL = "https://api-ec.cinef-expertises.com/api";
    const { getToken, isAuthenticated, userType, isLoading, deconnexion } = useAuth();

    const [isSidebarOpen, setIsSidebarOpen] = useState(false);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [monthlyData, setMonthlyData] = useState([]);
    const [selectedMonth, setSelectedMonth] = useState(null);
    const [selectedYear, setSelectedYear] = useState("2025");

    // Vérification d'authentification (seulement après le chargement initial)
    useEffect(() => {
        if (!isLoading && (!isAuthenticated || userType !== 'client')) {
            console.log('❌ MonthlyData: Accès non autorisé, déconnexion...');
            deconnexion();
        }
    }, [isAuthenticated, userType, isLoading, deconnexion]);

    // Charger toutes les données mensuelles du client connecté
    useEffect(() => {
        if (isAuthenticated && userType === 'client') {
            loadData();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isAuthenticated, userType]);

    const loadData = async () => {
        try {
            const token = getToken();
            if (!token) {
                deconnexion();
                return;
            }

            console.log(token);
            // console.log('Token client:', token);
            const res = await fetch(`${API_URL}/donnees-mensuelles/mon-client`, {
                method: "GET",
                headers: {
                    "Authorization": `Bearer ${token}`,
                    "Accept": "application/json",
                },
            });                

                if (!res.ok) {
                    const errorText = await res.text();
                    console.error('Erreur HTTP:', res.status, errorText);
                    throw new Error("Impossible de charger vos données.");
                }

                const data = await res.json();
                console.log('Données reçues:', data);
                
                // Vérifier que nous avons bien reçu un array de données
                const monthlyDataArray = Array.isArray(data?.data) ? data.data : 
                                       Array.isArray(data) ? data : 
                                       [];
                                       
                setMonthlyData(monthlyDataArray);

                // Sélectionner automatiquement le dernier mois disponible
                if (monthlyDataArray.length > 0) {
                    setSelectedMonth(monthlyDataArray[0]);
                }

            } catch (err) {
                setError(err.message);
                if (err.message.includes('401') || err.message.includes('Unauthorized')) {
                    deconnexion();
                }
            } finally {
                setLoading(false);
            }
        };

    // Calculer l'évolution en pourcentage
    const calculateEvolution = (current, previous) => {
        if (!previous || previous === 0) return 0;
        return (((current - previous) / previous) * 100).toFixed(2);
    };

    // Formater les montants
    const formatAmount = (amount) => {
        return new Intl.NumberFormat('fr-FR', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
        }).format(amount || 0);
    };

    // Préparer les données pour le graphique d'évolution
    const prepareChartData = () => {
        if (!monthlyData || monthlyData.length === 0) return null;

        // Trier les données par année et mois
        const sortedData = [...monthlyData].sort((a, b) => {
            if (a.annee !== b.annee) return a.annee - b.annee;
            return a.mois - b.mois;
        });

        const months = ['Jan', 'Fév', 'Mar', 'Avr', 'Mai', 'Jun', 
                       'Jul', 'Aoû', 'Sep', 'Oct', 'Nov', 'Déc'];

        const labels = sortedData.map(item => `${months[item.mois - 1]} ${item.annee}`);
        const caData = sortedData.map(item => item.ca_mensuel / 1000000); // En millions
        const resultatData = sortedData.map(item => item.resultat_mensuel / 1000000); // En millions

        return {
            labels,
            datasets: [
                {
                    label: 'Chiffre d\'Affaires (M XOF)',
                    data: caData,
                    borderColor: '#3b82f6',
                    backgroundColor: 'rgba(59, 130, 246, 0.1)',
                    borderWidth: 3,
                    pointRadius: 6,
                    pointBackgroundColor: '#3b82f6',
                    pointBorderColor: '#ffffff',
                    pointBorderWidth: 2,
                    tension: 0.4,
                },
                {
                    label: 'Résultat (M XOF)',
                    data: resultatData,
                    borderColor: '#f59e0b',
                    backgroundColor: 'rgba(245, 158, 11, 0.1)',
                    borderWidth: 3,
                    pointRadius: 6,
                    pointBackgroundColor: '#f59e0b',
                    pointBorderColor: '#ffffff',
                    pointBorderWidth: 2,
                    tension: 0.4,
                }
            ]
        };
    };

    // Configuration du graphique
    const chartOptions = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: {
                position: 'top',
                labels: {
                    usePointStyle: true,
                    padding: 20,
                    font: {
                        size: 14,
                        weight: '600'
                    }
                }
            },
            tooltip: {
                backgroundColor: 'rgba(0, 0, 0, 0.8)',
                titleColor: '#ffffff',
                bodyColor: '#ffffff',
                borderColor: '#e2e8f0',
                borderWidth: 1,
                cornerRadius: 8,
                displayColors: true,
                callbacks: {
                    label: function(context) {
                        return `${context.dataset.label}: ${context.parsed.y.toFixed(2)} M XOF`;
                    }
                }
            }
        },
        scales: {
            y: {
                beginAtZero: true,
                grid: {
                    color: '#f1f5f9'
                },
                ticks: {
                    font: {
                        size: 12
                    },
                    color: '#64748b',
                    callback: function(value) {
                        return `${value} M`;
                    }
                }
            },
            x: {
                grid: {
                    display: false
                },
                ticks: {
                    font: {
                        size: 12
                    },
                    color: '#64748b'
                }
            }
        },
        interaction: {
            intersect: false,
            mode: 'index'
        }
    };

    const chartData = prepareChartData();

    return (
        <div className="app-container">
            <Sidebar
                isOpen={isSidebarOpen}
                onClose={() => setIsSidebarOpen(false)}
            />

            <div className="main-wrapper">
                <Navbar />

                <main className="main-content">
                    {/* En-tête avec sélecteur */}
                    <div className="header-section">
                        <div>
                            <h1 className="page-title">Données Mensuelles</h1>
                            <p className="page-subtitle">Suivi des performances financières mensuelles de votre entreprise</p>
                        </div>
                        {/* <div className="year-selector">
                            <select 
                                value={selectedYear} 
                                onChange={(e) => setSelectedYear(e.target.value)}
                                className="year-select"
                            >
                                <option value="2025">Août 2025</option>
                                <option value="2024">2024</option>
                                <option value="2023">2023</option>
                            </select>
                        </div> */}
                    </div>

                    {/* Navigation des mois */}
                    <div className="month-tabs">
                        {monthlyData.map((m) => (
                            <button
                                key={m.id}
                                className={`month-tab ${selectedMonth?.id === m.id ? 'active' : ''}`}
                                onClick={() => setSelectedMonth(m)}
                            >
                                <span className="tab-icon">📊</span> Données mensuelles
                            </button>
                        ))}
                        
                    </div>

                    {error && <div className="alert alert-error">{error}</div>}
                    {loading && <div className="alert alert-loading">Chargement...</div>}

                    {!loading && monthlyData.length === 0 && (
                        <div className="no-data">Aucune donnée disponible pour le moment.</div>
                    )}

                    {selectedMonth && (
                        <>
                            {/* KPI Cards */}
                            <div className="kpi-grid">
                                <div className="kpi-card blue">
                                    <div className="kpi-label">CA du Mois</div>
                                    <div className="kpi-value">{formatAmount(selectedMonth.ca_mensuel)} XOF</div>
                                    <div className="kpi-evolution positive">
                                        ↗ {selectedMonth.evolution?.ca_evolution || '+8,4%'}
                                    </div>
                                </div>

                                <div className="kpi-card purple">
                                    <div className="kpi-label">CA Cumulé</div>
                                    <div className="kpi-value">{formatAmount(selectedMonth.ca_cumule)} XOF</div>
                                    <div className="kpi-evolution positive">
                                        ↗ {selectedMonth.evolution?.ca_cumule_evolution || '+16,5%'}
                                    </div>
                                </div>

                                <div className="kpi-card orange">
                                    <div className="kpi-label">Résultat Mensuel</div>
                                    <div className="kpi-value">{formatAmount(selectedMonth.resultat_mensuel)} XOF</div>
                                    <div className="kpi-evolution negative">
                                        ↘ {selectedMonth.evolution?.resultat_evolution || '-8,7%'}
                                    </div>
                                </div>

                                <div className="kpi-card green">
                                    <div className="kpi-label">Trésorerie</div>
                                    <div className="kpi-value">{formatAmount(selectedMonth.tresorerie)} XOF</div>
                                    <div className="kpi-evolution positive">
                                        ↗ {selectedMonth.evolution?.tresorerie_evolution || '+8,5%'}
                                    </div>
                                </div>
                            </div>

                            {/* Données Financières Détaillées */}
                            <div className="section-card">
                                <h2 className="section-title">Données Financières Détaillées</h2>

                                {/* Chiffre d'Affaires */}
                                <div className="subsection">
                                    <h3 className="subsection-title">CHIFFRE D'AFFAIRES</h3>
                                    <div className="data-row">
                                        <span className="data-label">CA du mois en cours</span>
                                        <span className="data-value blue">{formatAmount(selectedMonth.ca_mensuel)} XOF</span>
                                    </div>
                                    <div className="data-row">
                                        <span className="data-label">CA cumulé depuis janvier</span>
                                        <span className="data-value blue">{formatAmount(selectedMonth.ca_cumule)} XOF</span>
                                    </div>
                                </div>

                                {/* Résultats */}
                                <div className="subsection">
                                    <h3 className="subsection-title">RÉSULTATS</h3>
                                    <div className="data-row">
                                        <span className="data-label">Résultat mensuel</span>
                                        <span className="data-value orange">{formatAmount(selectedMonth.resultat_mensuel)} XOF</span>
                                    </div>
                                    <div className="data-row">
                                        <span className="data-label">Résultat cumulé</span>
                                        <span className="data-value orange">{formatAmount(selectedMonth.resultat_cumule)} XOF</span>
                                    </div>
                                </div>

                                {/* Trésorerie */}
                                <div className="subsection">
                                    <h3 className="subsection-title">TRÉSORERIE</h3>
                                    <div className="data-row">
                                        <span className="data-label">Solde de trésorerie en comptabilité</span>
                                        <span className="data-value green">{formatAmount(selectedMonth.tresorerie)} XOF</span>
                                    </div>
                                </div>
                            </div>

                            {/* Graphique Évolution Mensuelle */}
                            <div className="section-card">
                                <h2 className="section-title">Évolution Mensuelle</h2>
                                <div className="chart-container">
                                    {chartData ? (
                                        <div className="chart-wrapper">
                                            <Line data={chartData} options={chartOptions} />
                                        </div>
                                    ) : (
                                        <div className="chart-placeholder">
                                            <p>Aucune donnée disponible pour le graphique</p>
                                            <p className="chart-note">Les données d'évolution apparaîtront ici une fois disponibles</p>
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* Commentaire du Cabinet */}
                            <div className="section-card">
                                <h2 className="section-title">💬 Commentaire du Cabinet</h2>
                                <div className="comment-content">
                                    <p>{selectedMonth.commentaire || "Excellente performance ce mois-ci avec une croissance soutenue du chiffre d'affaires, la trésorerie reste solide. Nous recommandons de maintenir cette dynamique pour les prochains mois."}</p>
                                    <div className="comment-footer">
                                        <span className="comment-date">
                                            Mis à jour le {new Date().toLocaleDateString('fr-FR')}
                                        </span>
                                        <span className="comment-author">Cabinet CINEF-EC</span>
                                    </div>
                                </div>
                            </div>
                        </>
                    )}

                </main>
            </div>

            {/* STYLE */}
            <style>{`
                * { margin: 0; padding: 0; box-sizing: border-box; }
                
                .app-container { 
                    display: flex; 
                    min-height: 100vh; 
                    background: #f5f7fa; 
                    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
                }
                
                .main-wrapper { 
                    flex: 1; 
                    margin-left: 320px; 
                }
                
                .main-content { 
                    padding: 30px 40px;
                    max-width: 1400px;
                }

                /* Header */
                .header-section {
                    display: flex;
                    justify-content: space-between;
                    align-items: flex-start;
                    margin-bottom: 25px;
                }

                .page-title {
                    font-size: 32px;
                    font-weight: 700;
                    color: #c53030;
                    margin-bottom: 8px;
                }

                .page-subtitle {
                    font-size: 15px;
                    color: #64748b;
                }

                .year-selector {
                    margin-top: 8px;
                }

                .year-select {
                    padding: 10px 20px;
                    border: 1px solid #e2e8f0;
                    border-radius: 8px;
                    background: white;
                    font-size: 14px;
                    cursor: pointer;
                    color: #334155;
                }

                /* Tabs Navigation */
                .month-tabs {
                    display: flex;
                    gap: 10px;
                    margin-bottom: 30px;
                    border-bottom: 2px solid #e2e8f0;
                    padding-bottom: 0;
                }

                .month-tab {
                    padding: 12px 24px;
                    border: none;
                    background: transparent;
                    color: #64748b;
                    font-size: 14px;
                    cursor: pointer;
                    border-bottom: 3px solid transparent;
                    transition: all 0.2s;
                    display: flex;
                    align-items: center;
                    gap: 8px;
                }

                .month-tab:hover {
                    color: #008A44;
                }

                .month-tab.active {
                    color: #008A44;
                    border-bottom-color: #008A44;
                    font-weight: 600;
                }

                .tab-icon {
                    font-size: 16px;
                }

                /* KPI Cards */
                .kpi-grid {
                    display: grid;
                    grid-template-columns: repeat(4, 1fr);
                    gap: 20px;
                    margin-bottom: 30px;
                }

                .kpi-card {
                    background: white;
                    padding: 24px;
                    border-radius: 12px;
                    border-left: 4px solid;
                    box-shadow: 0 1px 3px rgba(0,0,0,0.06);
                    transition: transform 0.2s, box-shadow 0.2s;
                }

                .kpi-card:hover {
                    transform: translateY(-2px);
                    box-shadow: 0 4px 12px rgba(0,0,0,0.1);
                }

                .kpi-card.blue { border-left-color: #3b82f6; }
                .kpi-card.purple { border-left-color: #8b5cf6; }
                .kpi-card.orange { border-left-color: #f59e0b; }
                .kpi-card.green { border-left-color: #10b981; }

                .kpi-label {
                    font-size: 13px;
                    color: #64748b;
                    margin-bottom: 12px;
                    font-weight: 500;
                }

                .kpi-value {
                    font-size: 26px;
                    font-weight: 700;
                    color: #1e293b;
                    margin-bottom: 10px;
                }

                .kpi-evolution {
                    font-size: 13px;
                    font-weight: 600;
                    display: inline-flex;
                    align-items: center;
                    gap: 4px;
                }

                .kpi-evolution.positive { color: #10b981; }
                .kpi-evolution.negative { color: #ef4444; }

                /* Section Card */
                .section-card {
                    background: white;
                    border-radius: 12px;
                    padding: 30px;
                    margin-bottom: 25px;
                    box-shadow: 0 1px 3px rgba(0,0,0,0.06);
                }

                .section-title {
                    font-size: 18px;
                    font-weight: 700;
                    color: #1e293b;
                    margin-bottom: 25px;
                    padding-bottom: 15px;
                    border-bottom: 2px solid #f1f5f9;
                }

                /* Subsection */
                .subsection {
                    margin-bottom: 30px;
                }

                .subsection:last-child {
                    margin-bottom: 0;
                }

                .subsection-title {
                    font-size: 13px;
                    font-weight: 700;
                    color: #475569;
                    letter-spacing: 0.5px;
                    margin-bottom: 15px;
                    text-transform: uppercase;
                }

                .data-row {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    padding: 14px 0;
                    border-bottom: 1px solid #f1f5f9;
                }

                .data-row:last-child {
                    border-bottom: none;
                }

                .data-label {
                    font-size: 14px;
                    color: #475569;
                }

                .data-value {
                    font-size: 16px;
                    font-weight: 700;
                }

                .data-value.blue { color: #3b82f6; }
                .data-value.orange { color: #f59e0b; }
                .data-value.green { color: #10b981; }

                /* Chart */
                .chart-container {
                    min-height: 400px;
                    padding: 20px;
                    background: white;
                    border-radius: 8px;
                }

                .chart-wrapper {
                    position: relative;
                    height: 350px;
                    width: 100%;
                }

                .chart-placeholder {
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    justify-content: center;
                    height: 300px;
                    background: #f8fafc;
                    border-radius: 8px;
                    border: 2px dashed #cbd5e1;
                    text-align: center;
                    color: #94a3b8;
                }

                .chart-note {
                    font-size: 13px;
                    margin-top: 8px;
                }

                /* Comment */
                .comment-content {
                    background: #f8fafc;
                    padding: 20px;
                    border-radius: 8px;
                    border-left: 4px solid #3b82f6;
                }

                .comment-content p {
                    color: #475569;
                    line-height: 1.7;
                    margin-bottom: 15px;
                }

                .comment-footer {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    font-size: 12px;
                    color: #94a3b8;
                }

                .comment-author {
                    font-weight: 600;
                    color: #64748b;
                }

                /* Alerts */
                .alert {
                    padding: 16px;
                    border-radius: 8px;
                    margin-bottom: 20px;
                }

                .alert-error {
                    background: #fee2e2;
                    color: #dc2626;
                    border-left: 4px solid #dc2626;
                }

                .alert-loading {
                    background: #dbeafe;
                    color: #2563eb;
                    border-left: 4px solid #2563eb;
                }

                .no-data {
                    text-align: center;
                    padding: 60px;
                    color: #94a3b8;
                    font-size: 16px;
                }

                /* Responsive */
                @media (max-width: 1200px) {
                    .kpi-grid {
                        grid-template-columns: repeat(2, 1fr);
                    }
                }

                @media (max-width: 768px) {
                    .kpi-grid {
                        grid-template-columns: 1fr;
                    }
                    
                    .main-wrapper {
                        margin-left: 0;
                    }
                    
                    .header-section {
                        flex-direction: column;
                        gap: 15px;
                    }
                }
            `}</style>
        </div>
    );
};

export default MonthlyData;